import { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useCreateAiFeedback, CreateAiFeedbackInput, SummaryContentData } from './useAiFeedback';

export interface FeedbackHandlerOptions {
  defaultCommentPrefix?: string;
}

export interface UseFeedbackHandlerReturn {
  feedbackModalOpen: boolean;
  feedbackSnackbarOpen: boolean;
  feedbackLoading: boolean;
  feedbackError: any;
  feedbackSuccess: boolean;
  feedbackErrorMessage: string;
  openFeedbackModal: () => void;
  closeFeedbackModal: () => void;
  submitFeedback: (
    category: string,
    comments: string,
    summaryContent: SummaryContentData,
    traceId?: string,
    spanId?: string,
  ) => Promise<void>;
  closeFeedbackSnackbar: () => void;
}

export const useFeedbackHandler = (options: FeedbackHandlerOptions = {}): UseFeedbackHandlerReturn => {
  const { defaultCommentPrefix = 'AI feedback' } = options;

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSnackbarOpen, setFeedbackSnackbarOpen] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [feedbackErrorMessage, setFeedbackErrorMessage] = useState('');

  // Get patient ID from route
  const {
    params: { id: patientId },
  } = useRouteMatch<{ id: string }>();

  // AI Feedback hook
  const { createFeedback, loading: feedbackLoading, error: feedbackError } = useCreateAiFeedback();

  const openFeedbackModal = () => {
    setFeedbackModalOpen(true);
  };

  const closeFeedbackModal = () => {
    setFeedbackModalOpen(false);
  };

  const closeFeedbackSnackbar = () => {
    setFeedbackSnackbarOpen(false);
    setFeedbackErrorMessage('');
  };

  const submitFeedback = async (
    category: string,
    comments: string,
    summaryContent: SummaryContentData,
    traceId?: string,
    spanId?: string,
  ) => {
    if (!patientId) return;

    try {
      // Get pathname without base URL
      const page = window.location.pathname;

      const feedbackInput: CreateAiFeedbackInput = {
        patientId,
        summaryContent:
          summaryContent && typeof summaryContent === 'object' && 'contents' in summaryContent
            ? (summaryContent.contents as SummaryContentData)
            : summaryContent,
        page,
        category,
        comments: comments || `${defaultCommentPrefix} (${category})`,
        traceId,
        spanId,
      };

      await createFeedback(feedbackInput);

      // Success
      setFeedbackSuccess(true);
      setFeedbackSnackbarOpen(true);
      setFeedbackModalOpen(false);
    } catch (error) {
      // Error
      const errorMsg =
        error instanceof Error ? error.message : 'An unexpected error occurred while submitting feedback.';
      setFeedbackSuccess(false);
      setFeedbackErrorMessage(errorMsg);
      setFeedbackSnackbarOpen(true);
    }
  };

  return {
    feedbackModalOpen,
    feedbackSnackbarOpen,
    feedbackLoading,
    feedbackError,
    feedbackSuccess,
    feedbackErrorMessage,
    openFeedbackModal,
    closeFeedbackModal,
    submitFeedback,
    closeFeedbackSnackbar,
  };
};

export default useFeedbackHandler;
